
import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import BentoGrid from './components/BentoGrid';
import Stack from './components/Stack';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import Preloader from './components/Preloader';
import { motion, AnimatePresence } from 'framer-motion';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<'home' | 'projects'>('home');

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500); // Duración exacta de 2.5 segundos
    
    return () => clearTimeout(timer);
  }, []);

  // Sincronizar el scroll al cambiar de vista
  useEffect(() => {
    if (!isLoading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [view, isLoading]);

  return (
    <div className="min-h-screen selection:bg-[#39FF14] selection:text-black bg-[#121212] overflow-x-hidden">
      <AnimatePresence mode="wait">
        {isLoading ? (
          <Preloader key="preloader" />
        ) : (
          <motion.div key="main-content">
            <Navbar currentView={view} setView={setView} />
            
            <AnimatePresence mode="wait">
              {view === 'home' ? (
                <motion.div
                  key="home"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Hero setView={setView} />
                  <BentoGrid />
                  <Stack />
                  <ContactForm />
                </motion.div>
              ) : (
                <motion.div
                  key="projects-view"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="pt-32 pb-20 min-h-screen"
                >
                  <div className="max-w-7xl mx-auto px-6 lg:px-20 mb-12">
                    <button 
                      onClick={() => setView('home')}
                      className="text-[#39FF14] text-[10px] font-bold uppercase tracking-[0.3em] mb-8 hover:opacity-70 transition-opacity flex items-center gap-2 cursor-pointer"
                    >
                      <span>←</span> VOLVER AL INICIO
                    </button>
                    <h1 className="font-syncopate text-4xl md:text-6xl font-bold mb-4">PORTFOLIO <span className="text-[#39FF14]">COMPLETO</span></h1>
                    <p className="text-gray-400 max-w-2xl text-sm md:text-base">
                      Explora una selección de mis trabajos más recientes, enfocados en la excelencia técnica y el diseño de vanguardia.
                    </p>
                  </div>
                  <BentoGrid hideHeader />
                </motion.div>
              )}
            </AnimatePresence>
            
            <Footer currentView={view} setView={setView} />
            <WhatsAppButton />
            
            {/* Indicador de scroll solo en home */}
            {view === 'home' && (
              <div className="fixed bottom-10 left-6 md:left-10 hidden sm:flex flex-col items-center gap-4 z-40 pointer-events-none">
                <div className="w-[1px] h-12 md:h-20 bg-gradient-to-t from-[#39FF14] to-transparent" />
                <span className="[writing-mode:vertical-rl] text-[8px] md:text-[10px] font-bold tracking-[0.3em] md:tracking-[0.5em] text-[#39FF14]/60 uppercase">
                  DESLIZA PARA EXPLORAR
                </span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;
